<script setup lang="ts">
  import {
    ToastClose,
    ToastDescription,
    ToastProvider,
    ToastRoot,
    ToastTitle,
    ToastViewport
  } from 'reka-ui'
  import { Icon } from '@iconify/vue'
  import { cn } from '@/utils'
  import { DEFAULT_TOAST_DURATION, type ToastVariant, useToastStore } from '@/stores/toast'

  const toastStore = useToastStore()

  const viewportClass =
    'fixed right-4 top-4 z-50 flex w-[calc(100vw-2rem)] max-w-sm flex-col gap-3 list-none p-0 outline-none md:right-6 md:top-6'

  const baseRootClass =
    'group pointer-events-auto relative w-full overflow-hidden rounded-xl border bg-card px-4 py-3.5 shadow-shadow-3 transition-all ' +
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ' +
    'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 origin-top-right'

  const variantRootClasses: Record<ToastVariant, string> = {
    success: 'border-additional-green-primary/30',
    error: 'border-additional-red-primary/30',
    warning: 'border-additional-orange-primary/30',
    info: 'border-additional-blue-primary/30'
  }

  const variantAccentClasses: Record<ToastVariant, string> = {
    success: 'bg-additional-green-primary',
    error: 'bg-additional-red-primary',
    warning: 'bg-additional-orange-primary',
    info: 'bg-additional-blue-primary'
  }

  const variantIconClasses: Record<ToastVariant, string> = {
    success: 'bg-additional-green-secondary text-additional-green-primary',
    error: 'bg-additional-red-secondary text-additional-red-primary',
    warning: 'bg-additional-orange-secondary text-additional-orange-primary',
    info: 'bg-additional-blue-secondary text-additional-blue-primary'
  }

  const variantIcons: Record<ToastVariant, string> = {
    success: 'lucide:check',
    error: 'lucide:x',
    warning: 'lucide:triangle-alert',
    info: 'lucide:info'
  }

  function handleOpenChange(id: string, open: boolean) {
    if (!open) {
      toastStore.dismiss(id)
    }
  }
</script>

<template>
  <ToastProvider :duration="DEFAULT_TOAST_DURATION" :disable-swipe="true">
    <ToastViewport :class="viewportClass" />

    <ToastRoot
      v-for="toast in toastStore.toasts"
      :key="toast.id"
      v-model:open="toast.open"
      :duration="toast.duration"
      :class="cn(baseRootClass, variantRootClasses[toast.variant])"
      @update:open="(open) => handleOpenChange(toast.id, open)"
    >
      <template #default="{ remaining, duration }">
        <div class="flex w-full items-center gap-3">
          <span
            :class="
              cn(
                'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                variantIconClasses[toast.variant]
              )
            "
          >
            <Icon :icon="variantIcons[toast.variant]" class="h-4 w-4" />
          </span>

          <div class="grid min-w-0 flex-1 gap-0.5">
            <ToastTitle
              class="break-words text-[15px] font-semibold leading-tight text-shade-primary"
            >
              {{ toast.title }}
            </ToastTitle>
            <ToastDescription
              v-if="toast.description"
              class="break-words text-sm text-shade-tertiary"
            >
              {{ toast.description }}
            </ToastDescription>
          </div>

          <ToastClose
            class="ml-auto inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-shade-quaternary transition hover:bg-bg-tertiary hover:text-shade-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label="Close"
          >
            <Icon icon="lucide:x" class="h-4 w-4" />
          </ToastClose>
        </div>

        <div class="absolute inset-x-0 bottom-0 h-0.5 bg-bg-tertiary">
          <div
            :class="
              cn(
                'h-full transition-[width] duration-100 ease-linear',
                variantAccentClasses[toast.variant]
              )
            "
            :style="{
              width: `${duration ? Math.max(0, Math.min(100, (remaining / duration) * 100)) : 0}%`
            }"
          />
        </div>
      </template>
    </ToastRoot>
  </ToastProvider>
</template>
